import makeStyles from '@mui/styles/makeStyles';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {user} from '../../../store/reducers/user';
import AgyleButton from '../../../components/ui/buttons/agyleButton';
import {useTranslation} from 'react-i18next';
import Formatter from '../../../util/formatter';
import moment from 'moment';
import Grid from '@mui/material/Grid';
import Notes from '../../../util/notes';
import AgyleBlockSwitch from '../../../components/ui/switch/agyleBlockSwitch';
import AgyleSelectField from '../../../components/ui/selector/agyleSelectField';
import {MenuItem} from '@mui/material';
import InformationBox from '../../../components/other/informationBox';
import _, {isNull} from 'lodash';
import {setSnackbar} from '../../../store/reducers/globals';
import MiniFormDialog from '../../../components/modal/miniFormDialog';
import {formSmokeIsValid} from '../forms/formsUtil';

const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: theme.palette.background + ` !important`,
    },
    outcome: {
        border: '1px solid #7199E6',
        background: '#cfdffd',
        color: '#3F73D6',
        display: 'flex',
        padding: 10,
        justifyContent: 'center',
        gap: 15,

    },
    auditImg: {
        width: '100%', maxWidth: 700,
    },
    scores: {
        fontSize: 20, fontWeight: 'bold', textAlign: 'right',
    },
    outcomeLabel: {
        fontWeight: 'bold',
        fontSize: 15,
        color: '#3F73D6',
    },
    groupRoot: {
        marginBottom: 10,
        padding: 5,
        borderRadius: 5,
    },
    paper: {
        padding: 10,
        backgroundColor: '#252525',
    },
    centerRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
    },
    userLog: {
        fontSize: 11,
        fontStyle: 'italic',
        color: theme.palette.mode === 'light' ? '#4B4B4B' : '#818181',
    },
    comment: {
        fontSize: 15,
        padding: 5,
        backgroundColor: theme.palette.background.default,
        borderRadius: 5,
    },
    clinician: {
        marginBottom: 10,
    },
    row: {
        width: '100%',
        marginBottom: 10,
    },
    divider: {
        margin: '10px 0',
    },
    label: {
        color: theme.palette.inPatient.questionText,
        fontSize: 12,
        fontWeight: 600,
        marginLeft: 10,
        marginBottom: 5,
    },
    switchLabel: {
        fontSize: 14,
        fontWeight: 600,
        paddingLeft: 10,
    },
}));

export function SmokingForm(props) {
    const classes = useStyles();
    const {t} = useTranslation();

    const userObject = useSelector(user);
    const tester = Formatter.formatUserFullName(userObject);

    const smokeLevels = ['less than 10', '10 or more'];
    let smokingFormInit = {
        smoker: null,
        cigarettesPerDay: '',
        who: '',
        date: '',
    };
    const [form, setForm] = useState(smokingFormInit);

    useEffect(() => {
        setForm((formLast) => {
            return {
                ...formLast,
                who: tester,
                date: moment().toISOString(),
            };
        });
    }, [userObject]);


    useEffect(() => {
        if (props.formData) {
            let formClone = _.cloneDeep(props.formData);
            setForm({
                ...formClone,
                who: tester,
                date: moment().toISOString(),
            });
        }

    }, []);

    useEffect(() => {
        if (props.changeFormData) {
            props.changeFormData(form);
        }
        if (props.formIsValid) {
            props.formIsValid(formSmokeIsValid(form));
        }
    }, [form]);


    const handleFieldChange = (value, path) => {
        setForm((formLast) => {
            return {
                ...formLast,
                [path]: value,
            };
        });
        if (props.formIsChanged) props.formIsChanged(true);
    };

    const renderCustomOption = (opt) => {
        return (
            <MenuItem
                key={opt}
                value={opt}
            >
                {opt}
            </MenuItem>
        );
    };

    return (
        <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
                <InformationBox type="info">
                    {t('Smoker = smoked in last 28 days')}
                </InformationBox>
            </Grid>

            <Grid item xs={6}>
                <div className={classes.switchLabel}>
                    {t('Smoking status')}
                </div>
            </Grid>

            <Grid item xs={6}>
                <div>
                    <AgyleBlockSwitch
                        customLabels={{yes: t('Smoker'), alternative: t('Ex-Smoker'), no: t('Never Smoked')}}
                        valueObject={{true: 'Smoker', alt: 'Ex-Smoker', false: 'Never Smoked'}}
                        fourCases
                        onChange={(e) => handleFieldChange(e, 'smoker')}
                        value={form.smoker}
                    />
                </div>
            </Grid>

            {form.smoker === 'Smoker' &&
                <>
                    <Grid item xs={12}>
                        <InformationBox type="info">
                            {t('ADVISE: The best way of stopping smoking is with a combination of medication and specialist support both of which will be offered to you while you are in hospital.')}
                        </InformationBox>
                    </Grid>

                    <Grid item xs={12}>
                        <div className={classes.label}> Cigarettes per day?</div>
                        <AgyleSelectField
                            onChange={(e) => handleFieldChange(e, 'cigarettesPerDay')}
                            options={smokeLevels}
                            withBackground
                            value={form.cigarettesPerDay}
                            padding="12px"
                            customObject={renderCustomOption}
                            style={{marginLeft: 10}}
                            withBorder
                            noBorder={false}
                            withNewBackground
                            size="large"
                        />
                    </Grid>
                </>
            }
        </Grid>
    );
}

function SmokingFormModal(props) {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const [formData, setFormData] = useState(null);

    const handleSubmit = () => {
        if (isNull(formData.smoker)) {
            dispatch(setSnackbar({type: 'warning', message: 'Please select one of the available options.'}));
        } else {
            if (props.onChange) {
                props.onChange(
                    [
                        {
                            name: 'smoking',
                            value: formData,
                            noteToAdd: Notes.getSmokingNote(formData),
                        },
                    ],
                );
            }
            if (props.onClose) props.onClose();
        }
    };

    const removeOxygen = () => {
        if (props.onChange) {
            props.onChange(
                [
                    {
                        name: 'smoking',
                        value: null,
                        noteToAdd: Notes.getSmokingNote(null),
                    },
                ],
            );
        }
        if (props.onClose) props.onClose();
    };

    return (
        <MiniFormDialog
            open={props.open}
            onClose={props.onClose}
            title={t(props._formTitle)}
            disableSubmit={props.disableSave}
            onSave={handleSubmit}
            fullWidth
            width="md"
            closeButtonOnHeader
            buttons={[
                <AgyleButton
                    disabled={props.disableSave}
                    onClick={removeOxygen}
                    key="delete"
                >
                    {t('Delete')}
                </AgyleButton>,
            ]}
        >
            <SmokingForm
                formData={props.patient?.smoking}
                changeFormData={setFormData}
            />
        </MiniFormDialog>
    );
}

export default SmokingFormModal;